import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { BasePopupActionPayload, BasePopupProps } from 'contexts/PopupContext';
import { UserProfile } from 'types/interfaces/user/UserProfile';

import { useCreditsAnalytics } from 'hooks/analytics/useCreditsAnalytics';
import { getCreditsCurrencySelector } from 'store/payment/selectors';

import { ReactComponent as FlameIcon } from 'assets/icons/flame.svg';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { IconedPopup } from 'components/UI/IconedPopup';

import css from './userProfileUnlockPopup.module.sass';

type ActionsPayload =
  | BasePopupActionPayload<'submit'>
  | BasePopupActionPayload<'close'>;

interface DataProps {
  user: UserProfile;
  isSubmitting: boolean;
  isThreadPage?: boolean;
  messegePhoto?: string;
}

type Props = BasePopupProps<DataProps, (action: ActionsPayload) => void>;

export const UserProfileUnlockPopup: React.FC<Props> = ({ data, onAction }) => {
  const { t } = useTranslation('app');

  const creditsCurrency = useSelector(getCreditsCurrencySelector);

  const { trackVerticaCreditProfileUnlock } = useCreditsAnalytics();

  const handleSubmit = () => {
    trackVerticaCreditProfileUnlock({
      profileId: data.user.id,
      operation: 'click',
    });
    onAction({ type: 'submit' });
  };

  const handleClose = () => {
    trackVerticaCreditProfileUnlock({
      profileId: data.user.id,
      operation: 'close',
    });

    !data.isSubmitting && onAction({ type: 'close' });
  };

  useEffect(() => {
    trackVerticaCreditProfileUnlock({
      profileId: data.user.id,
      operation: 'show',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPhotoUrl = () => {
    const { messegePhoto, isThreadPage } = data;

    if (isThreadPage && messegePhoto) {
      return messegePhoto;
    }

    return data.user.photos.find((photo) => photo.is_main)?.profile_url || '';
  };

  return (
    <IconedPopup icon={getPhotoUrl()} zIndex={99} onClose={handleClose}>
      <p className={css.username}>
        <FlameIcon className={css.hotIcon} />
        {`${data.user.username}, ${data.user.age}`}
      </p>
      <p className={css.description}>
        {t('userProfile.unlockPopup.title', {
          userName: data.user.username,
        })}
      </p>
      <p className={css.price}>
        {t('userProfile.unlockPopup.description', {
          creditsCurrency,
        })}
      </p>
      <BaseButton
        type={ButtonTypes.Accent}
        className={css.button}
        loading={data.isSubmitting}
        onClick={handleSubmit}
      >
        {t('userProfile.unlockPopup.submit')}
      </BaseButton>
    </IconedPopup>
  );
};
